import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Tag, ArrowRight, X, MapPin, Clock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { mockData } from '../mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve image URLs - handles both relative and absolute paths
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

const News = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [articles, setArticles] = useState([]);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);

  const categories = ['all', 'Community Events', 'Programs', 'Success Stories', 'Announcements'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [newsRes, eventsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/news?published_only=true`),
        axios.get(`${BACKEND_URL}/api/events?upcoming_only=true`)
      ]);
      setArticles(newsRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      setArticles(mockData.newsArticles);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail || !newsletterEmail.includes('@')) {
      toast.error('Please enter a valid email address.');
      return;
    }
    
    setIsSubscribing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/submissions/newsletter`, { email: newsletterEmail });
      toast.success('Successfully subscribed! Thank you for joining our newsletter.');
      setNewsletterEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info('You are already subscribed to our newsletter.');
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    } finally {
      setIsSubscribing(false);
    }
  };

  const displayArticles = articles.length > 0 ? articles : mockData.newsArticles;

  const filteredArticles = selectedCategory === 'all'
    ? displayArticles
    : displayArticles.filter(article => article.category === selectedCategory);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="news-page">
      {/* Hero Section */}
      <section className="relative py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img 
            src="https://images.pexels.com/photos/518543/pexels-photo-518543.jpeg?auto=compress&cs=tinysrgb&w=1600"
            alt="News"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A345E]/90 to-[#007A7A]/85"></div>
        </div>
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">News & Events</h1>
          <p className="text-xl text-white/95 leading-relaxed">
            Stay updated with the latest stories, events, and impact from Gateway Inc.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-[#F5F2E8] border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-200 ${
                  selectedCategory === category
                    ? 'bg-[#007A7A] text-white shadow-md'
                    : 'bg-white text-gray-600 hover:bg-[#E8E2D0]'
                }`}
                data-testid={`category-${category}`}
              >
                {category === 'all' ? 'All News' : category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* News Articles */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading ? (
            <div className="text-center py-12">Loading news...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="news-articles-list">
              {filteredArticles.map((article) => (
                <Link to={`/news/${article.id}`} key={article.id} className="block">
                  <Card className="h-full hover:shadow-xl transition-all duration-300 border-0 group cursor-pointer bg-[#F5F2E8]" data-testid={`news-article-${article.id}`}>
                    {article.image && (
                      <div className="relative h-36 overflow-hidden rounded-t-lg">
                        <img 
                          src={resolveImageUrl(article.image)} 
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <CardHeader className="p-4 pb-2">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#007A7A]/10 text-[#0A345E]">
                          <Tag className="w-3 h-3 mr-1" />
                          {article.category}
                        </span>
                      </div>
                      <CardTitle className="text-base leading-tight group-hover:text-[#007A7A] transition-colors duration-200 text-[#0A345E]">
                        {article.title}
                      </CardTitle>
                      <CardDescription className="flex items-center text-xs text-gray-500 mt-1">
                        <Calendar className="w-3 h-3 mr-1" />
                        {article.date || formatDate(article.published_date)}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">{article.excerpt}</p>
                      <div className="flex items-center text-[#007A7A] font-medium text-xs group-hover:translate-x-1 transition-transform duration-200">
                        Read More <ArrowRight className="w-3 h-3 ml-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}

          {filteredArticles.length === 0 && !isLoading && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No articles found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-16 bg-[#F5F2E8]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-bold text-[#0A345E] mb-4">Upcoming Events</h2>
            <p className="text-lg text-gray-600">Join us in making a difference</p>
          </div>

          <div className="max-w-5xl mx-auto" data-testid="events-list">
            {events.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {events.map((event, index) => {
                  const colors = ['#007A7A', '#85955D', '#D1C3A2', '#0A345E'];
                  const borderColor = colors[index % colors.length];
                  return (
                    <Card 
                      key={event.id} 
                      className="border-l-4 shadow-md hover:shadow-lg transition-all duration-300 bg-white cursor-pointer hover:scale-[1.02]"
                      style={{ borderLeftColor: borderColor }}
                      onClick={() => setSelectedEvent(event)}
                      data-testid={`event-${event.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <h3 className="text-base font-semibold text-[#0A345E] line-clamp-1 flex-1 pr-3">
                            {event.title}
                          </h3>
                          <div className="text-sm font-medium whitespace-nowrap" style={{ color: borderColor }}>
                            {formatDate(event.event_date)}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg shadow-lg">
                <Calendar className="w-16 h-16 mx-auto text-[#007A7A]/50 mb-4" />
                <h3 className="text-xl font-semibold text-[#0A345E] mb-2">No Upcoming Events</h3>
                <p className="text-gray-600">Check back soon for new events and community gatherings.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Event Detail Modal */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent className="max-w-lg">
          {selectedEvent && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-[#0A345E] pr-6">
                  {selectedEvent.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Date & Time */}
                <div className="flex items-center text-[#007A7A] font-medium">
                  <Calendar className="w-5 h-5 mr-2" />
                  <span>{formatDate(selectedEvent.event_date)}</span>
                  {(selectedEvent.start_time || selectedEvent.end_time) && (
                    <span className="ml-2 text-gray-600">
                      • {selectedEvent.start_time}{selectedEvent.end_time && ` - ${selectedEvent.end_time}`}
                    </span>
                  )}
                </div>

                {/* Location */}
                {selectedEvent.location && (
                  <div className="flex items-start text-gray-600">
                    <MapPin className="w-5 h-5 mr-2 mt-0.5 text-[#85955D]" />
                    <span>{selectedEvent.location}</span>
                  </div>
                )}

                {/* Description */}
                {selectedEvent.description && (
                  <div className="pt-2 border-t">
                    <p className="text-gray-700 leading-relaxed">{selectedEvent.description}</p>
                  </div>
                )}

                {/* Category */}
                {selectedEvent.category && (
                  <div className="pt-2">
                    <span className="inline-block bg-[#007A7A]/10 text-[#007A7A] text-sm px-3 py-1 rounded-full">
                      {selectedEvent.category}
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Newsletter Signup */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img 
            src="https://images.unsplash.com/photo-1527525443983-6e60c75fff46?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Community"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A345E]/90 to-[#007A7A]/85"></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-4">Stay Updated</h2>
            <p className="text-xl text-white/95 mb-8">
              Subscribe to our newsletter for the latest news, events, and stories of hope.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 justify-center">
              <input
                type="email"
                placeholder="Enter your email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="flex-1 max-w-md px-6 py-4 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
                data-testid="newsletter-email-input"
                required
              />
              <button 
                type="submit"
                disabled={isSubscribing}
                className="px-8 py-4 bg-white text-[#0A345E] font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-200 shadow-lg disabled:opacity-50" 
                data-testid="newsletter-subscribe-btn"
              >
                {isSubscribing ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default News;
